import { AccessTime } from '@mui/icons-material';
import {
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListSubheader,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EmptyState from 'components/EmptyState';
import TimelineIcon from 'components/TimelineIcon';
import TimelineList from 'components/TimelineList';
import dayjs from 'dayjs';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getNonNullishValue } from 'services/helper';
import { SummaryServiceOrder } from 'types/serviceOrder.types';
import { TimelineTypeEnum } from 'types/timeline.types';

function SummaryServiceOrders({
  serviceOrders,
  loading,
}: {
  serviceOrders?: SummaryServiceOrder[];
  loading: boolean;
}) {
  const { t } = useTranslation('_common');

  const haveData = serviceOrders && (serviceOrders?.length || 0) > 0;
  const isEmpty = !loading && !haveData;

  return (
    <Paper
      variant="outlined"
      sx={{
        pt: 2,
        // height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: '1 1 auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          flex: '0 0 auto',
        }}
      >
        <Typography variant="h6">{t('service_orders')}</Typography>
        <Button component={Link} to="/events">
          {t('see_all')}
        </Button>
      </Box>
      {loading && <TimelineList.Loading />}
      {haveData && <ListContent serviceOrders={serviceOrders} />}
      {isEmpty && <ListEmpty />}
    </Paper>
  );
}

function ListEmpty() {
  const { t } = useTranslation(['_common', 'service_order']);
  return (
    <EmptyState
      sx={{ flex: '1 1 auto', p: 0, mt: 2, overflow: 'auto' }}
      title={t('_common:there_is_still_nothing_here')}
      description={t('service_order:there_is_no_service_order')}
    />
  );
}

function ListContent({ serviceOrders }: { serviceOrders: SummaryServiceOrder[] }) {
  const { t } = useTranslation('_common');

  const normalizedEvents = React.useMemo(() => {
    return serviceOrders
      .map((serviceOrder) => serviceOrder.date.slice(0, '2022-06-29'.length))
      .reduce(
        (dates, currentDate) => (!dates.includes(currentDate) ? [...dates, currentDate] : dates),
        [] as string[],
      )
      .map((date) => ({
        date,
        values: serviceOrders.filter(
          (serviceOrder) => serviceOrder.date.slice(0, '2022-06-29'.length) === date,
        ),
      }));
  }, [serviceOrders]);

  return (
    <List
      sx={{
        flex: '1 1 auto',
        p: 0,
        mt: 2,
        overflow: 'auto',
      }}
    >
      {normalizedEvents.map((dateServiceOrders) => (
        <Fragment key={dateServiceOrders.date}>
          <ListSubheader sx={{ bgcolor: 'grey.200' }}>
            {dayjs(dateServiceOrders.date).calendar(dayjs(), {
              sameDay: `[${t('today')}]`,
              lastDay: 'DD MMM',
              nextDay: 'DD MMM',
              lastWeek: 'DD MMM',
              nextWeek: 'DD MMM',
              sameElse: 'DD MMM',
            })}
          </ListSubheader>
          {dateServiceOrders.values.map((serviceOrder, index) => (
            <Fragment key={serviceOrder.id}>
              <ListItemButton
                component={Link}
                to={`/service-orders/${serviceOrder.id}/${serviceOrder.accountId}`}
              >
                <ListItemIcon>
                  <TimelineIcon type={TimelineTypeEnum.SERVICE_ORDER} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    getNonNullishValue(serviceOrder.cause) ||
                    getNonNullishValue(serviceOrder.defect) ||
                    getNonNullishValue(serviceOrder.description)
                  }
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <AccessTime sx={{ width: '0.8rem', height: '0.8rem', mr: 0.5 }} />
                      {dayjs(serviceOrder.date).format('LTS')}
                      <Box
                        sx={{
                          bgcolor: 'text.disabled',
                          width: 4,
                          height: 4,
                          borderRadius: '4px',
                          mx: 0.5,
                        }}
                      />
                      <Box
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {`${t('account')}: ${serviceOrder.accountCode} - ${
                          serviceOrder.accountName
                        }`}
                      </Box>
                    </Box>
                  }
                />
              </ListItemButton>
              {index < dateServiceOrders.values.length - 1 && <Divider variant="middle" />}
            </Fragment>
          ))}
        </Fragment>
      ))}
    </List>
  );
}

export default SummaryServiceOrders;
